// Domänenmodell der Szene: Teile in mm, engine-frei (kein Three.js-Import).
// Feldnamen entsprechen dem Server-JSON (snake_case), um Mapping zu vermeiden.

import { planeAxisField } from "./sketch"
import { normalizeVariables } from "./variables"

export function newPart(attrs = {}) {
  const part = {
    name: "Platte",
    material_id: null,
    length_mm: 800,
    width_mm: 600,
    thickness_mm: 19,
    pos_x: 0, pos_y: 0, pos_z: 0,
    rot_x: 0, rot_y: 0, rot_z: 0,
    meta: {},
    ...attrs
  }
  // ||= statt ?? im Spread: auch explizites undefined/null ergibt eine frische UUID
  part.client_id ||= generateClientId()
  return part
}

// crypto.randomUUID existiert nur in sicheren Kontexten (HTTPS/localhost) —
// beim Test über http://<LAN-IP> braucht es den Fallback über getRandomValues.
export function generateClientId() {
  if (crypto.randomUUID) return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

// Eine Skizze = eigene Referenzebene + Shapes; ein Projekt kann mehrere haben
export function newSketch(attrs = {}) {
  const sketch = { name: "Skizze 1", plane: { normal: "y+", offset: 0 }, shapes: [], ...attrs }
  sketch.id ||= generateClientId()
  return sketch
}

export default class SceneDocument {
  constructor() {
    this.parts = new Map()
    this.materials = new Map()
    this.camera = null
    this.sketches = []
    this.activeSketchId = null
    // Raummaß (Nische/Raumbegrenzung): { enabled: true, width_mm, height_mm,
    // depth_mm } oder {} — persistiert wie camera im Projekt
    this.room = {}
    // Parametrische Variablen { name: value_mm } — projektweite benannte Maße,
    // an die Teil-Maße gebunden werden (siehe editor/variables.js)
    this.variables = {}
    this.listeners = new Set()
  }

  // --- Skizzen -----------------------------------------------------------------

  get activeSketch() {
    return this.sketches.find((s) => s.id === this.activeSketchId) ?? null
  }

  // Kompatibilitäts-Sichten: bestehender Code liest und mutiert die aktive Skizze
  get sketch() {
    return this.activeSketch?.shapes ?? []
  }

  get sketchPlane() {
    return this.activeSketch ? this.resolvedSketchPlane(this.activeSketch) : { normal: "y+", offset: 0 }
  }

  // Effektive Ebene einer Skizze: ist sie an ein Teil gekoppelt (plane.ref
  // mit { client_id, axis_pos }), wandert der Offset mit der aktuellen
  // Achsposition des Teils mit; Teil gelöscht → ref ignorieren, Ebene statisch
  resolvedSketchPlane(sketch) {
    const plane = sketch?.plane ?? { normal: "y+", offset: 0 }
    const part = plane.ref ? this.parts.get(plane.ref.client_id) : null
    if (!part) return plane
    return { ...plane, offset: plane.offset + (part[planeAxisField(plane)] - plane.ref.axis_pos) }
  }

  set sketchPlane(plane) {
    if (this.activeSketch) this.activeSketch.plane = plane
  }

  addSketch() {
    const numbers = this.sketches.map((s) => parseInt(s.name.replace(/\D+/g, ""), 10) || 0)
    const sketch = newSketch({ name: `Skizze ${Math.max(0, ...numbers) + 1}` })
    this.sketches.push(sketch)
    this.activeSketchId = sketch.id
    return sketch
  }

  removeSketch(id) {
    this.sketches = this.sketches.filter((s) => s.id !== id)
    if (this.activeSketchId === id) this.activeSketchId = this.sketches[0]?.id ?? null
  }

  setActiveSketch(id) {
    if (this.sketches.some((s) => s.id === id)) this.activeSketchId = id
  }

  hasSketchShapes() {
    return this.sketches.some((s) => s.shapes.length > 0)
  }

  getMaterial(id) {
    return this.materials.get(id)
  }

  onChange(fn) {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  emit(event) {
    this.listeners.forEach((fn) => fn(event))
  }

  getPart(clientId) {
    return this.parts.get(clientId)
  }

  allParts() {
    return [...this.parts.values()]
  }

  // Mutatoren — nur über Commands aufrufen (Ausnahme: loadFrom beim Initial-Load)

  insertPart(part) {
    this.parts.set(part.client_id, part)
    this.emit({ type: "part:added", clientId: part.client_id })
  }

  updatePart(clientId, attrs) {
    const part = this.parts.get(clientId)
    if (!part) return
    Object.assign(part, attrs)
    this.emit({ type: "part:updated", clientId })
  }

  removePart(clientId) {
    this.parts.delete(clientId)
    this.emit({ type: "part:removed", clientId })
  }

  setCamera(camera) {
    this.camera = camera
  }

  // Variablenstand als Ganzes tauschen (nur über SetVariablesCommand aufrufen)
  setVariables(variables) {
    this.variables = variables
    this.emit({ type: "variables:changed" })
  }

  loadFrom(json) {
    this.parts = new Map(json.parts.map((p) => [p.client_id, newPart(p)]))
    this.materials = new Map((json.materials || []).map((m) => [m.id, m]))
    this.camera = json.camera
    this.room = json.room || {}
    this.variables = normalizeVariables(json.variables)
    // sketch-Formate: v1 reines Shape-Array, v2 { plane_y, shapes },
    // v3 { plane: { normal, offset }, shapes },
    // v4 { sketches: [{ id, name, plane, shapes }], active_id }
    const raw = json.sketch
    if (raw?.sketches) {
      this.sketches = raw.sketches.map((s) => newSketch(s))
      this.activeSketchId = this.sketches.some((s) => s.id === raw.active_id)
        ? raw.active_id
        : this.sketches[0]?.id ?? null
    } else {
      const shapes = (Array.isArray(raw) ? raw : raw?.shapes) || []
      const plane = !Array.isArray(raw) && raw?.plane?.normal
        ? { normal: raw.plane.normal, offset: raw.plane.offset || 0 }
        : { normal: "y+", offset: (!Array.isArray(raw) && raw?.plane_y) || 0 }
      this.sketches = shapes.length > 0 ? [ newSketch({ plane, shapes }) ] : []
      this.activeSketchId = this.sketches[0]?.id ?? null
    }
    this.emit({ type: "loaded" })
  }

  toJSON() {
    return {
      camera: this.camera,
      sketch: { sketches: this.sketches, active_id: this.activeSketchId },
      room: this.room,
      variables: this.variables,
      parts: this.allParts()
    }
  }
}
